import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import { useSubcircles } from "./useSubcircles";
import { useCircleUsers } from "./useUserData";
const MAX_SUBCIRCLES_FOR_ALIAS_PREFETCH = 25;

interface UseUserAliasFiltersArgs {
  selectedSubcircleId: string;
  isNoSubcirclesFilter: boolean;
  activeSubcircleId: string;
}

export function useUserAliasFilters({
  selectedSubcircleId,
  isNoSubcirclesFilter,
  activeSubcircleId,
}: UseUserAliasFiltersArgs) {
  const { user: authUser, token } = useAuth();
  const { subcircles } = useSubcircles();
  const {
    circleUsers,
    isLoading: isLoadingSubcircleMembers,
    error: subcircleMembersError,
  } = useCircleUsers(activeSubcircleId);

  const subcircleMemberIds = useMemo(() => {
    if (!activeSubcircleId || isLoadingSubcircleMembers) {
      return null;
    }
    return new Set(circleUsers.map((member) => member.userId));
  }, [activeSubcircleId, circleUsers, isLoadingSubcircleMembers]);

  const subcircleAliasPrefetchQueries = useQueries({
    queries: subcircles.map((circle) => ({
      queryKey: ["subcircleUsers", authUser?.schoolId, circle.circleId],
      queryFn: async () => {
        if (!authUser?.schoolId || !token) {
          throw new Error("Missing organization or token");
        }
        return api.circles.getSubCircleUsers(
          circle.circleId,
          authUser.schoolId,
          token
        );
      },
      enabled:
        !!authUser?.schoolId &&
        !!token &&
        subcircles.length > 0 &&
        (isNoSubcirclesFilter ||
          (!selectedSubcircleId &&
            subcircles.length <= MAX_SUBCIRCLES_FOR_ALIAS_PREFETCH)),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isAliasDataLoading = useMemo(() => {
    if (activeSubcircleId) {
      return isLoadingSubcircleMembers;
    }
    if (isNoSubcirclesFilter) {
      return subcircleAliasPrefetchQueries.some((query) => query.isLoading);
    }
    if (
      subcircles.length === 0 ||
      subcircles.length > MAX_SUBCIRCLES_FOR_ALIAS_PREFETCH
    ) {
      return false;
    }
    return subcircleAliasPrefetchQueries.some((query) => query.isLoading);
  }, [
    activeSubcircleId,
    isNoSubcirclesFilter,
    isLoadingSubcircleMembers,
    subcircles.length,
    subcircleAliasPrefetchQueries,
  ]);

  const allSubcircleMemberIds = useMemo(() => {
    const ids = new Set<string>();
    subcircleAliasPrefetchQueries.forEach((query) => {
      query.data?.users?.forEach((member) => ids.add(member.userId));
    });
    return ids;
  }, [subcircleAliasPrefetchQueries]);

  const aliasByUserId = useMemo(() => {
    const map: Record<string, string> = {};

    const addAlias = (userId: string, alias?: string) => {
      const trimmed = alias?.trim();
      if (!trimmed || map[userId]) return;
      map[userId] = trimmed;
    };

    if (activeSubcircleId) {
      circleUsers.forEach((member) => addAlias(member.userId, member.alias));
      return map;
    }

    subcircleAliasPrefetchQueries.forEach((query) => {
      query.data?.users?.forEach((member) =>
        addAlias(member.userId, member.alias)
      );
    });

    return map;
  }, [activeSubcircleId, circleUsers, subcircleAliasPrefetchQueries]);

  const selectedSubcircleName = useMemo(() => {
    if (!selectedSubcircleId) {
      return "All subcircles";
    }
    if (isNoSubcirclesFilter) {
      return "No subcircles";
    }
    return (
      subcircles.find((circle) => circle.circleId === selectedSubcircleId)
        ?.name || "Subcircle"
    );
  }, [selectedSubcircleId, isNoSubcirclesFilter, subcircles]);

  const isUserListLoading =
    (!!activeSubcircleId && isLoadingSubcircleMembers) ||
    (isNoSubcirclesFilter && isAliasDataLoading);

  return {
    subcircles,
    subcircleMemberIds,
    allSubcircleMemberIds,
    aliasByUserId,
    isAliasDataLoading,
    isUserListLoading,
    isLoadingSubcircleMembers,
    subcircleMembersError,
    selectedSubcircleName,
  };
}
